import { FormEvent, KeyboardEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  type CreatePersonaInput,
  type Persona,
  type PersonaLanguage,
  type Tone,
} from '@/stores/personasStore';

const toneOptions: Array<{ value: Tone; label: string }> = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'witty', label: 'Witty' },
  { value: 'inspirational', label: 'Inspirational' },
  { value: 'technical', label: 'Technical' },
];

const languageOptions: Array<{ value: PersonaLanguage; label: string }> = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
];

interface PersonaFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  persona?: Persona;
  isLoading?: boolean;
  onClose: () => void;
  onSave: (data: CreatePersonaInput) => Promise<void>;
}

interface FormState {
  name: string;
  tone: Tone;
  style_notes: string;
  target_audience: string;
  topics: string[];
  language: PersonaLanguage;
}

function getInitialState(persona?: Persona): FormState {
  return {
    name: persona?.name ?? '',
    tone: (persona?.tone as Tone) ?? 'professional',
    style_notes: persona?.style_notes ?? '',
    target_audience: persona?.target_audience ?? '',
    topics: persona?.topics ?? [],
    language: (persona?.language as PersonaLanguage) ?? 'it',
  };
}

export function PersonaFormModal({
  isOpen,
  mode,
  persona,
  isLoading = false,
  onClose,
  onSave,
}: PersonaFormModalProps) {
  const [form, setForm] = useState<FormState>(getInitialState(persona));
  const [topicInput, setTopicInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const modalTitle = mode === 'create' ? 'Nuova Persona' : 'Modifica Persona';

  const canAddTopics = form.topics.length < 10;

  useEffect(() => {
    if (isOpen) {
      setForm(getInitialState(persona));
      setTopicInput('');
      setError(null);
    }
  }, [isOpen, persona]);

  if (!isOpen) {
    return null;
  }

  const addTopic = () => {
    const normalized = topicInput.trim();
    if (!normalized) {
      return;
    }
    if (form.topics.some((topic) => topic.toLowerCase() === normalized.toLowerCase())) {
      setError('Topic gia presente.');
      return;
    }
    if (form.topics.length >= 10) {
      setError('Puoi inserire massimo 10 topic.');
      return;
    }

    setForm((prev) => ({ ...prev, topics: [...prev.topics, normalized] }));
    setTopicInput('');
    setError(null);
  };

  const removeTopic = (topicToRemove: string) => {
    setForm((prev) => ({
      ...prev,
      topics: prev.topics.filter((topic) => topic !== topicToRemove),
    }));
  };

  const handleTopicKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTopic();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError('Il nome e obbligatorio.');
      return;
    }

    const payload: CreatePersonaInput = {
      name: form.name.trim(),
      tone: form.tone,
      style_notes: form.style_notes.trim(),
      target_audience: form.target_audience.trim(),
      topics: form.topics,
      language: form.language,
    };

    try {
      await onSave(payload);
      onClose();
    } catch (saveError) {
      if (saveError instanceof Error) {
        setError(saveError.message);
      } else {
        setError('Errore durante il salvataggio della persona.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <Card className="relative w-full max-w-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <CardHeader>
          <CardTitle>{modalTitle}</CardTitle>
        </CardHeader>

        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="persona-name">
                Nome *
              </label>
              <Input
                id="persona-name"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Es. Personal Brand Alessio"
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="persona-tone">
                  Tono di voce
                </label>
                <select
                  id="persona-tone"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={form.tone}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tone: event.target.value as Tone,
                    }))
                  }
                >
                  {toneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="persona-language">
                  Lingua
                </label>
                <select
                  id="persona-language"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  value={form.language}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      language: event.target.value as PersonaLanguage,
                    }))
                  }
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="persona-style-notes">
                Style notes
              </label>
              <textarea
                id="persona-style-notes"
                className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                value={form.style_notes}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    style_notes: event.target.value,
                  }))
                }
                placeholder="Descrivi lo stile da usare"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="persona-target-audience">
                Target audience
              </label>
              <textarea
                id="persona-target-audience"
                className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                value={form.target_audience}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    target_audience: event.target.value,
                  }))
                }
                placeholder="Per chi stai scrivendo"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="persona-topic-input">
                Topics (max 10)
              </label>
              <div className="flex gap-2">
                <Input
                  id="persona-topic-input"
                  value={topicInput}
                  onChange={(event) => setTopicInput(event.target.value)}
                  onKeyDown={handleTopicKeyDown}
                  placeholder="Aggiungi un topic"
                  disabled={!canAddTopics}
                />
                <Button type="button" variant="outline" onClick={addTopic} disabled={!canAddTopics}>
                  Aggiungi
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {form.topics.map((topic) => (
                  <button
                    type="button"
                    key={topic}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs',
                      'hover:bg-accent',
                    )}
                    onClick={() => removeTopic(topic)}
                  >
                    {topic}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                Annulla
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvataggio...' : mode === 'create' ? 'Salva Persona' : 'Aggiorna Persona'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
